import * as React from 'react';

import { useAppSelector } from '@/redux/hooks';
import {
    getItemSummaryImageVersion,
    getItemSummaryPhotos,
    getItemSummarySellerId,
    getItemSummaryTitle,
} from '@/redux/modules/item/summary/itemSummary.selectors';
import { getImageUrl } from '@/utils/urls';

export type UseConsoleCarouselDataProps = {
    catalogId: number;
    initialIndex: number;
    itemId: number;
};

type Photos = {
    alt: string;
    src: string;
}[];

export type UseConsoleCarouselData = {
    canNext: boolean;
    canPrev: boolean;
    currentImageIndex: number;
    gotoNext: () => void;
    gotoPrevious: () => void;
    mappedPhotos: Photos;
    title: string;
    totalPhotosCount: number;
};

const useConsoleCarouselData = ({
    catalogId,
    initialIndex,
    itemId,
}: UseConsoleCarouselDataProps): UseConsoleCarouselData => {
    const imageVersion = useAppSelector((state) => getItemSummaryImageVersion(state, itemId));
    const photos = useAppSelector((state) => getItemSummaryPhotos(state, itemId));
    const sellerId = useAppSelector((state) => getItemSummarySellerId(state, itemId));
    const title = useAppSelector((state) => getItemSummaryTitle(state, itemId));

    const [currentImageIndex, setCurrentImageIndex] = React.useState(initialIndex);
    const isPopout = initialIndex !== 0;

    React.useEffect(() => {
        // When the itemId changes, shift back to the first image
        if (!isPopout && itemId) {
            setCurrentImageIndex(0);
        }
    }, [itemId, isPopout]);

    const canPrev = currentImageIndex > 0;
    const canNext = currentImageIndex + 1 < photos.length;

    const gotoNext = () => {
        canNext ? setCurrentImageIndex(currentImageIndex + 1) : null;
    };

    const gotoPrevious = () => {
        canPrev ? setCurrentImageIndex(currentImageIndex - 1) : null;
    };

    const mapSlidePhotos = React.useCallback(
        (imageNumber, index) => {
            const alt = index ? `${title} - ${index + 1}` : title;
            const imageUrl = getImageUrl({
                catalogId,
                imageNumber,
                imageVersion: imageVersion,
                itemId,
                sellerId: sellerId,
                width: 1024,
            });
            const mappedPhoto = {
                alt,
                src: imageUrl,
            };

            return mappedPhoto;
        },
        [catalogId, imageVersion, itemId, sellerId, title]
    );

    let mappedPhotos: Photos;

    if (photos.length) {
        mappedPhotos = photos.map(mapSlidePhotos);
    }

    return {
        canNext,
        canPrev,
        currentImageIndex,
        gotoNext,
        gotoPrevious,
        mappedPhotos,
        title,
        totalPhotosCount: photos.length,
    };
};

export default useConsoleCarouselData;
